import React from 'react'

import {
  ArrowDownIcon as RadixArrowDownIcon,
  ArrowLeftIcon as RadixArrowLeftIcon,
  ArrowRightIcon as RadixArrowRightIcon,
  ArrowUpIcon as RadixArrowUpIcon,
} from '@radix-ui/react-icons'
import { motion } from 'framer-motion'
import { navigate } from 'gatsby'
import tw from 'twin.macro'

import { ButtonBase, ModLink } from '../components'

const StatsBackground = tw.div` w-full h-full col-span-full top-0 left-0 right-0 bottom-0 -z-1 absolute`
// Newest components
const MxSectionContainer = tw.div`grid grid-cols-6 md:grid-cols-16 items-center mb-8 md:mb-0`
const MxSectionHeroContainer = tw.div`grid grid-cols-6 items-center mb-8 text-gray-600 font-light md:(grid-cols-16 mb-0 px-6) lg:px-8 2xl:px-0`
const MxSectionHeroFirstContainer = tw.div`col-start-1 col-end-7 h-full`
const MxSectionHeroSecondContainer = tw.div`mb-auto row-start-1 col-span-full md:(col-start-8 col-end-17 auto-rows-min)`
const MxSectionHeroTitle = tw.h1`font-thin leading-tighter text-4xl max-w-lg pt-10 md:pt-0 pb-3`
const MxSectionFirstContainer = tw.div`col-start-1 col-end-7 md:col-start-1 md:col-end-9`
const MxSectionSecondContainer = tw.div`col-span-full row-start-1 md:col-start-9 md:col-end-17 md:grid md:grid-cols-8 md:auto-rows-min md:gap-y-6 md:gap-x-6`
const MxListStatsContainer = tw.div`relative gap-y-5 grid grid-cols-1 py-4 md:items-center md:py-4 md:grid-cols-9 lg:py-8 lg:space-y-0 lg:gap-y-0 lg:grid-cols-16`
const MxListStatContainer = tw.div`items-center grid gap-x-4 grid-cols-4 col-span-1 md:space-y-2 md:items-start md:space-x-0 md:flex md:flex-col md:col-span-3 lg:mb-0 lg:col-span-4`
const MxStatLabel = tw.p`text-sm`
const MxStatTitle = tw.p`text-gray-800 text-2xl font-thin leading-tight md:text-3xl lg:text-3xl`
const MxStatContainer = tw.div`space-y-1 col-span-3`
const MxTextContainer = tw.div`pl-4 lg:pl-10`
const MxSectionTextContainer = tw.div`px-4 md:pl-6 md:pr-0 lg:pl-8 mb-0 md:mb-12 lg:mb-24`
const MxSectionTitle = tw.h2`font-thin leading-tight text-4xl max-w-md w-5/6 text-black md:max-w-lg md:w-auto md:leading-tighter lg:max-w-xl`
const MxSectionDescription = tw.p`font-sans font-light text-gray-600 text-lg font-light leading-relaxed mt-8 md:(max-w-xl)`
const MxSectionLabel = tw.p`text-base mb-2`
const MxFirstDivider = tw.div`border-t pt-6 mt-6 md:mb-16 lg:mb-0 pl-4 lg:pl-10`

const labelColors = {
  green: tw`text-emerald-600`,
  gray: tw`text-gray-600`,
  red: tw`text-red-600`,
  blue: tw`text-blue-600`,
  yellow: tw`text-amber-600`,
  orange: tw`text-orange-600`,
  primary: tw`text-gray-600`,
}

const titleColors = {
  green: tw`text-emerald-900`,
  gray: tw`text-gray-900`,
  red: tw`text-red-900`,
  blue: tw`text-blue-900`,
  yellow: tw`text-amber-900`,
  orange: tw`text-orange-900`,
  primary: tw`text-gray-900`,
}

const MxSectionSvgContainer = tw.div`w-12 h-12 flex items-center justify-center shrink-0 col-span-1 mx-auto md:mx-0`

type MxSectionFirstContentProps = {
  title: string
  label: string
  description?: string
  color?: 'green' | 'gray' | 'red' | 'orange' | 'yellow' | 'blue'
}

const MxSectionFirstContent = ({
  title,
  label,
  description,
  color = 'gray',
}: MxSectionFirstContentProps) => {
  return (
    <MxSectionTextContainer>
      <MxSectionLabel css={[color && labelColors[color]]}>
        {label}
      </MxSectionLabel>
      <MxSectionTitle css={[color && titleColors[color]]}>
        {title}
      </MxSectionTitle>
      {description && (
        <MxSectionDescription css={[color && titleColors[color]]}>
          {description}
        </MxSectionDescription>
      )}
    </MxSectionTextContainer>
  )
}

const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.25,
    },
  },
}

const itemVariants = {
  show: {
    y: 0,
    opacity: 1,
    transition: {
      y: { stiffness: 1000, velocity: -100, ease: 'easeOut' },
    },
  },
  hidden: {
    y: 50,
    opacity: 0,
  },
}

const linkColors = {
  orange: tw`text-orange-medium visited:text-orange-medium`,
  olive: tw`text-emerald-700 visited:text-emerald-700`,
  gold: tw`text-amber-700 visited:text-amber-700`,
  rain: tw`text-blue-700 visited:text-blue-700`,
  red: tw`text-red-700 visited:text-red-700`,
  default: tw`text-gray-700 visited:text-gray-700`,
}

const getArrowIcon = (arrowDirection: string) => {
  switch (arrowDirection) {
    case 'up':
      return <RadixArrowUpIcon color="currentColor" />
    case 'down':
      return <RadixArrowDownIcon color="currentColor" />
    case 'left':
      return <RadixArrowLeftIcon color="currentColor" />
    default:
      return <RadixArrowRightIcon color="currentColor" />
  }
}

type LinkProps = {
  color?: 'orange' | 'olive' | 'gold' | 'rain' | 'red' | 'default'
  children: any
  arrowDirection?: 'down' | 'left' | 'up'
  onClick?: () => void
  to: string
  css?: any
}

const Link = ({ arrowDirection, children, to, color, ...props }: LinkProps) => {
  return (
    <ModLink
      to={to}
      tw="text-base md:space-x-2 inline-flex items-center"
      css={[color && linkColors[color]]}
      caps="true"
      {...props}
      onClick={props.onClick}
    >
      <span>{children}</span>
      {getArrowIcon(arrowDirection as string)}
    </ModLink>
  )
}

const Button = ({
  actions,
  ...props
}: {
  actions: { link: string; text: string }
  [x: string]: any
}) => {
  return (
    <ButtonBase
      onClick={() => {
        navigate(actions.link)
      }}
      size="marketing"
      loadingCentered={false}
      danger={false}
      {...props}
    >
      <span>{actions.text}</span>
      <RadixArrowRightIcon />
    </ButtonBase>
  )
}

const MxButton = ({
  onClick,
  children,
  color,
  loading,
  disabled,
  kind,
  ...props
}: {
  [x: string]: any
  color?:
    | 'orange'
    | 'olive'
    | 'rain'
    | 'gold'
    | 'red'
    | 'white'
    | 'whiteHover'
    | 'default'
}) => {
  return (
    <ButtonBase
      onClick={onClick}
      size="marketing"
      className={kind === 'link' ? 'group' : ''}
      kind={kind}
      loadingCentered={true}
      danger={false}
      css={[
        color && buttonColors[color],
        (loading || (disabled && true)) && buttonColors['disable'],
      ]}
      {...props}
    >
      {children && !loading ? (
        <>
          <span>{children}</span>
          <RadixArrowRightIcon />
        </>
      ) : (
        <span>One moment...</span>
      )}
      {kind === 'link' && (
        <span tw="absolute -bottom-1 left-0 w-0 !m-0 h-px bg-black transition-all group-hover:w-full"></span>
      )}
    </ButtonBase>
  )
}

const WebsiteButton = ({
  onClick,
  children,
  color,
  loading,
  disabled,
  kind,
  ...props
}: {
  [x: string]: any
  color?:
    | 'transparentWhite'
    | 'transparentBlack'
    | 'solidGray'
    | 'solidWhite'
    | 'solidDark'
}) => {
  return (
    <ButtonBase
      onClick={onClick}
      size="marketing"
      kind={kind}
      loadingCentered={true}
      danger={false}
      css={[
        color && buttonColors[color],
        (loading || (disabled && true)) && buttonColors['disable'],
      ]}
      {...props}
    >
      {loading ? <span>One moment...</span> : <span>{children}</span>}
    </ButtonBase>
  )
}

const buttonColors = {
  orange: tw`text-white bg-orange-medium hover:bg-gray-900`,
  olive: tw`text-white bg-emerald-800 hover:text-emerald-100 hover:bg-emerald-900`,
  gold: tw`text-white bg-amber-800 hover:text-amber-100 hover:bg-amber-900`,
  rain: tw`text-white bg-blue-800 hover:text-blue-100 hover:bg-blue-900`,
  red: tw`text-white bg-gray-800 hover:text-red-100 hover:bg-gray-900`,
  transparentWhite: tw`!w-fit !border-white !bg-transparent !px-10 !py-4 !h-10 md:!h-14 !text-base !text-white hover:!bg-gray-light hover:!text-gray-dark`,
  transparentBlack: tw`!w-fit !border-gray-dark !bg-transparent !px-10 !py-4 !h-10 md:!h-14 !text-base !text-gray-dark hover:!bg-gray-dark hover:!text-white`,
  solidGray: tw`!w-fit !bg-gray-light !px-10 !py-4 !text-base !h-10 md:!h-14 !text-gray-dark hover:!bg-white`,
  solidWhite: tw`!w-fit !bg-white !px-6 !py-4 !text-base !h-10 md:!h-14 !text-gray-dark hover:!bg-gray-200 hover:!text-gray-dark`,
  solidDark: tw`!w-fit !bg-gray-dark !px-6 !py-4 !text-base !h-10 md:!h-14 !text-white hover:!bg-black hover:!text-white`,
  whiteHover: tw`text-gray-900 bg-white hover:text-white hover:bg-gray-600`,
  white: tw`text-gray-900 bg-white hover:text-white hover:bg-gray-600`,
  default: tw`text-white bg-gray-900 hover:bg-gray-600 dark:hover:bg-gray-600`,
  disable: tw`opacity-50 pointer-events-none cursor-not-allowed`,
}

const ModButton = ({
  onClick,
  children,
  color,
  ...props
}: {
  [x: string]: any
  onClick?: any
  color?: 'orange' | 'olive' | 'rain' | 'gold' | 'red' | 'default'
}) => {
  return (
    <ButtonBase
      onClick={onClick}
      size="marketing"
      loadingCentered={false}
      danger={false}
      css={[color && buttonColors[color]]}
      {...props}
    >
      {children}
      <RadixArrowRightIcon tw="ml-2 relative top-0.5" />
    </ButtonBase>
  )
}

type ActionProps = {
  color?: 'orange' | 'olive' | 'gold' | 'rain' | 'default' | 'red'
  children: any
  action: any
}
const Action = ({ children, action, color, ...props }: ActionProps) => {
  return (
    <ModButton
      tw="inline-flex cursor-pointer"
      color={color}
      onClick={() => navigate(action)}
      {...props}
    >
      {children}
    </ModButton>
  )
}

{
  /* <Link css={[tw`md:hidden`]} to={action} color={color} {...props}>
  {children}
</Link> */
}

const ActionAlt = ({ children, action, color, ...props }: ActionProps) => {
  return (
    <>
      <ModButton
        tw="md:inline-flex cursor-pointer"
        color={color}
        onClick={action}
        kind=""
        {...props}
      >
        {children}
      </ModButton>
    </>
  )
}

const MotionMxListStatsContainer = motion(MxListStatsContainer)
const MotionMxListStatContainer = motion(MxListStatContainer)

export {
  StatsBackground,
  containerVariants,
  itemVariants,
  MxButton,
  Button,
  Link,
  Action,
  ActionAlt,
  MxSectionContainer,
  MxSectionHeroContainer,
  MxSectionHeroFirstContainer,
  MxSectionHeroSecondContainer,
  MxSectionHeroTitle,
  MxSectionFirstContainer,
  MxSectionSecondContainer,
  MotionMxListStatsContainer,
  MotionMxListStatContainer,
  MxStatLabel,
  MxStatTitle,
  MxTextContainer,
  MxSectionTextContainer,
  MxSectionTitle,
  MxSectionDescription,
  MxSectionLabel,
  MxFirstDivider,
  MxSectionSvgContainer,
  MxStatContainer,
  MxSectionFirstContent,
  WebsiteButton,
}
